/**
 * Geometrie pure des graphiques : echelles, graduations, segments.
 *
 * Aucun calcul metier ici — seulement de quoi poser des nombres que l'appelant
 * a fournis sur une surface. Tout est testable sans rendu.
 */

export type Maybe = number | null | undefined;

export const isValue = (value: Maybe): value is number => typeof value === 'number' && Number.isFinite(value);

/** Plus petit pas « rond » (1, 2, 5 x 10^k) qui vaut au moins `raw`. */
export const niceStep = (raw: number): number => {
  if (!(raw > 0) || !Number.isFinite(raw)) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / magnitude;
  const factor = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;

  return factor * magnitude;
};

export interface Domain {
  readonly min: number;
  readonly max: number;
  /** Valeurs des lignes de grille, de `min` a `max` inclus. */
  readonly ticks: readonly number[];
}

/**
 * Domaine vertical et graduations.
 *
 * Sans borne imposee, le haut est arrondi **vers le haut** a un pas rond :
 * jamais un maximum plus bas que la donnee. Avec une borne imposee (`max`), elle
 * est respectee sauf si la donnee la depasse, auquel cas c'est la donnee qui
 * gagne — un graphique qui rogne une valeur ment. Les valeurs negatives ne sont
 * pas le cas d'usage (des comptes) : le bas du domaine les inclut, mais 0 n'est
 * garanti dans l'axe que si la donnee n'est pas entierement negative.
 *
 * Avec `integers`, le pas vaut au moins 1 : sur 0 a 2, l'axe porte 0, 1, 2 et non
 * 0, 0,5, 1… — « 1,5 compte » n'existe pas. Sans valeur, l'axe va de 0 a 1.
 */
export const computeDomain = (
  dataMin: number,
  dataMax: number,
  options: {
    readonly min?: number;
    readonly max?: number;
    readonly intervals?: number;
    /** Toutes les valeurs sont entieres (des comptes) : aucune graduation a virgule. */
    readonly integers?: boolean;
  } = {},
): Domain => {
  const intervals = options.intervals ?? 4;
  const floor = Math.min(options.min ?? 0, dataMin);
  const ceiling = Math.max(options.max ?? Number.NEGATIVE_INFINITY, dataMax);

  if (options.max !== undefined && ceiling === options.max) {
    const step = (ceiling - floor) / intervals;

    return { min: floor, max: ceiling, ticks: Array.from({ length: intervals + 1 }, (_, index) => floor + step * index) };
  }

  const span = ceiling > floor ? ceiling - floor : 1;
  const step = options.integers === true ? Math.max(1, niceStep(span / intervals)) : niceStep(span / intervals);
  const niceMin = Math.floor(floor / step) * step;
  const niceMax = Math.max(Math.ceil(ceiling / step) * step, niceMin + step);
  const count = Math.round((niceMax - niceMin) / step);

  return {
    min: niceMin,
    max: niceMax,
    ticks: Array.from({ length: count + 1 }, (_, index) => Number((niceMin + step * index).toFixed(10))),
  };
};

/** Echelle lineaire d'un domaine vers un intervalle de pixels (l'ordonnee SVG descend). */
export const linearScale =
  (domainMin: number, domainMax: number, rangeStart: number, rangeEnd: number) =>
  (value: number): number =>
    domainMax === domainMin
      ? (rangeStart + rangeEnd) / 2
      : rangeStart + ((value - domainMin) / (domainMax - domainMin)) * (rangeEnd - rangeStart);

/**
 * Plages contigues de valeurs presentes : un `null` **coupe** la courbe. Le
 * relier de part et d'autre inventerait une valeur pour la periode manquante.
 */
export const presentRuns = (values: readonly Maybe[]): number[][] => {
  const runs: number[][] = [];
  let current: number[] = [];

  values.forEach((value, index) => {
    if (isValue(value)) {
      current.push(index);
    } else if (current.length > 0) {
      runs.push(current);
      current = [];
    }
  });

  if (current.length > 0) {
    runs.push(current);
  }

  return runs;
};

/** Rangs des etiquettes d'abscisse a ecrire : repartis, premier et dernier toujours inclus. */
export const labelIndexes = (count: number, maxLabels: number): number[] => {
  if (count <= 0) {
    return [];
  }

  if (count === 1 || maxLabels <= 1) {
    return [0];
  }

  const shown = Math.min(count, Math.max(2, maxLabels));

  return [...new Set(Array.from({ length: shown }, (_, rank) => Math.round((rank * (count - 1)) / (shown - 1))))];
};

const round = (value: number): number => Math.round(value * 100) / 100;

/** Trace `M x y L x y …` d'une plage de points. */
export const polylinePath = (points: readonly (readonly [number, number])[]): string =>
  points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${String(round(x))} ${String(round(y))}`).join(' ');
